import { PopupManager } from './popup';
import PopperJS from 'popper.js';

const stop = e => e.stopPropagation();

/**
 * @param {HTMLElement} [reference=$refs.reference] - The reference element used to position the popper.
 * @param {HTMLElement} [popper=$refs.popper] - The HTML element used as popper, or a configuration used to generate the popper.
 * @param {String} [placement=button] - Placement of the popper accepted values: top(-start, -end), right(-start, -end), bottom(-start, -end), left(-start, -end)
 * @param {Number} [offset=0] - Amount of pixels the popper will be shifted (can be negative).
 * @param {Boolean} [visible=false] Visibility of the popup element.
 * @param {Boolean} [visible-arrow=false] Visibility of the arrow, no style.
 */
export default {
  props: {
    transformOrigin: {
      type: [Boolean, String],
      default: true
    },
    placement: {
      type: String,
      default: 'bottom'
    },
    boundariesPadding: {
      type: Number,
      default: 5
    },
    reference: {},
    popper: {},
    offset: {
      default: 0
    },
    value: Boolean,
    visibleArrow: Boolean,
    appendToBody: {
      type: Boolean,
      default: true
    },
    tabToLeave: {
      type: Boolean,
      default: false
    },
    popperOptions: {
      type: Object,
      default() {
        return {
          gpuAcceleration: false
        };
      }
    },
    modifiers: Object
  },

  data() {
    return {
      showPopper: false,
      currentPlacement: ''
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.showPopper = val;
        this.$emit('input', val);
      }
    },

    showPopper(val) {
      if (this.disabled) return;
      if (val) {
        this.updatePopper();
      } else {
        this.destroyPopper();
      }
      this.$emit('input', val);
    }
  },

  methods: {
    createPopper() {
      if (this.$isServer) return;
      this.currentPlacement = this.currentPlacement || this.placement;
      if (!/^(top|bottom|left|right)(-start|-end)?$/g.test(this.currentPlacement)) {
        return;
      }

      const popper = this.popperElm = this.popperElm || this.popper || this.$refs.popper;
      let reference = this.referenceElm = this.referenceElm || this.reference || this.$refs.reference;

      if (!reference &&
        this.$slots.reference &&
        this.$slots.reference[0]) {
        reference = this.referenceElm = this.$slots.reference[0].elm;
      }

      if (!popper || !reference) return;
      if (this.visibleArrow) this.appendArrow(popper);
      if (this.appendToBody && !this.tabToLeave) document.body.appendChild(this.popperElm);
      if (this.popperJS && this.popperJS.destroy) {
        this.popperJS.destroy();
      }

      const options = {};
      options.modifiers = this.createModifiers(this.popperOptions);
      options.modifiers.offset = {
        offset: this.offset
      };
      options.placement = this.currentPlacement;
      options.positionFixed = typeof this.popperOptions.positionFixed === 'undefined'
        ? (typeof this.popperOptions.forceAbsolute === 'undefined')
          ? false
          : !this.popperOptions.forceAbsolute
        : this.popperOptions.positionFixed;
      options.onCreate = () => {
        this.$emit('created', this);
        this.resetTransformOrigin();
        this.$nextTick(this.updatePopper);
      };
      this.popperJS = new PopperJS(reference, popper, options);
      // this.popperJS.onCreate(_ => {
      //   this.$emit('created', this);
      //   this.resetTransformOrigin();
      //   this.$nextTick(this.updatePopper);
      // });
      // if (typeof options.onUpdate === 'function') {
      //   this.popperJS.onUpdate(options.onUpdate);
      // }
      if (this.popperJS.popper) {
        this.popperJS.popper.style.zIndex = PopupManager.nextZIndex(!!PopupManager.modalDom);
      }
      this.popperElm.addEventListener('click', stop);
    },

    createModifiers(popperOptions) {
      const modifiers = this.modifiers || {};
      const optionMap = {
        gpuAcceleration: 'computeStyle',
        boundariesElement: 'preventOverflow'
      };

      Object.keys(optionMap).forEach((key) => {
        if (typeof popperOptions[key] !== 'undefined' && typeof modifiers[optionMap[key]] === 'undefined') {
          modifiers[optionMap[key]] = {
            [key]: popperOptions[key]
          };
        }
      });

      return modifiers;
    },

    updatePopper() {
      const popperJS = this.popperJS;
      if (popperJS) {
        popperJS.enableEventListeners();
        popperJS.update();
        if (popperJS.popper) {
          popperJS.popper.style.zIndex = PopupManager.nextZIndex(!!PopupManager.modalDom);
        }
      } else {
        this.createPopper();
      }
    },

    updatePopperReference(reference) {
      if (this.popperJS) {
        this.popperJS.reference = reference;
      }
    },

    updatePopperBoundaries(el) {
      if (this.popperJS) {
        this.popperJS.options.modifiers.preventOverflow.boundariesElement = el;
        this.popperJS.modifiers.some((modifier) => {
          if (modifier.name === 'preventOverflow') {
            modifier.boundariesElement = el;
            return true;
          }
        });
      }
    },

    doDestroy(forceDestroy) {
      if (!this.popperJS || (this.showPopper && !forceDestroy)) return;
      this.popperJS.destroy();
      this.popperJS = null;
    },

    destroyPopper() {
      if (this.popperJS) {
        this.popperJS.disableEventListeners();
        this.resetTransformOrigin();
      }
    },

    resetTransformOrigin() {
      if (!this.transformOrigin || !this.popperJS.popper) {
        return;
      }
      let placementMap = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
      };
      let placement = this.popperJS.popper.getAttribute('x-placement').split('-')[0];
      let origin = placementMap[placement];
      this.popperJS.popper.style.transformOrigin = typeof this.transformOrigin === 'string'
        ? this.transformOrigin
        : ['top', 'bottom'].indexOf(placement) > -1 ? `center ${origin}` : `${origin} center`;
    },

    appendArrow(element) {
      let hash;
      if (this.appended) {
        return;
      }

      this.appended = true;

      for (let item in element.attributes) {
        if (/^_v-/.test(element.attributes[item].name)) {
          hash = element.attributes[item].name;
          break;
        }
      }

      const arrow = document.createElement('div');

      if (hash) {
        arrow.setAttribute(hash, '');
      }
      arrow.setAttribute('x-arrow', '');
      arrow.className = 'popper__arrow';
      element.appendChild(arrow);
    },

    doBeforeDestroy() {
      this.doDestroy(true);
      if (this.popperElm && this.popperElm.parentNode === document.body) {
        this.popperElm.removeEventListener('click', stop);
        document.body.removeChild(this.popperElm);
      } else if (window.__MICRO_APP_ENVIRONMENT__) {
        // in order to fix PWA issues
        // https://github.com/micro-zoe/micro-app/issues/277
        const microAppBody = document.querySelector(`micro-app[name="${window.__MICRO_APP_NAME__}"] micro-app-body`);
        if (this.popperElm && this.popperElm.parentNode === microAppBody) {
          this.popperElm.removeEventListener('click', stop);
          document.body.removeChild(this.popperElm);
        }
      }
    }
  },

  beforeDestroy() {
    this.doBeforeDestroy();
  },

  /**
   * call destroy in keep-alive mode
   *
   * Do not use this.$options.beforeDestroy[0].call(this) to call the beforeDestroy method of vue-popover.js,
   * if there is a global Vue.mixin() call in the project,
   * this method will not point to vue-popover.js beforeDestroy
   */
  deactivated() {
    // this.$options.beforeDestroy[0].call(this);
    this.doBeforeDestroy();
  }
};
